import { normalizeModulesResponse } from "../modules/moduleUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fetchWithRetry = async (url, options, retries = 1) => {
  try {
    const response = await fetch(url, options);

    if (response.status === 504 && retries > 0) {
      return fetchWithRetry(url, options, retries - 1);
    }

    return response;
  } catch (err) {
    if (retries > 0) {
      return fetchWithRetry(url, options, retries - 1);
    }
    throw err;
  }
};

export const fetchClientData = async (clientId, token) => {
  try {
    let allClients = [];
    let continuationToken = null;
    let totalSize = null;
    const firstUrl = `${API_BASE_URL}/bhp-client-portal/clients?clientId=${clientId}`;

    do {
      const url = continuationToken
        ? `${API_BASE_URL}/bhp-client-portal/clients?continuationToken=${continuationToken}&clientId=${clientId}`
        : firstUrl;

      const response = await fetchWithRetry(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error();

      const data = await response.json();

      if (totalSize === null && typeof data.totalSize === "number") {
        totalSize = data.totalSize;
      }

      if (data.clients) {
        allClients = [...allClients, ...data.clients];
      }

      continuationToken = data.continuationToken || null;
    } while (continuationToken);

    return {
      clients: allClients,
      totalSize: totalSize ?? allClients.length,
    };
  } catch {
    return null;
  }
};

const handle403Error = () => {
  console.warn("[403 Forbidden] Token may have expired");
};

export const lookupMatter = async (matterId, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/salesforce/matters/lookup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        filters: { Id: matterId },
        fields: [
          "Id",
          "Name",
          "litify_pm__Client__c",
          "Client_Name__c",
          "Client_CPF__c",
          "RetainerDateYN__c",
          "RetainerDate__c",
          "Questionnaire_Complete__c",
          "Questionnaire_Complete_Date__c",
          "Email__c",
          "Phone__c",
        ],
      }),
    });

    if (response.status === 403) return null;
    if (response.status === 500) {
      const t = await response.text();
      window.location.href = `/error?message=${encodeURIComponent(t)}`;
      return null;
    }
    if (!response.ok) throw new Error();

    return await response.json();
  } catch {
    return null;
  }
};

export const lookupParty = async (partyId, token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/salesforce/parties/lookup`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        filters: { Id: partyId },
        fields: [
          "Id",
          "Name",
          "litify_pm__Date_of_birth__c",
          "CPF__c",
          "litify_pm__Email__c",
          "Phone",
          "litify_pm__Phone_Home__c",
          "litify_pm__Phone_Mobile__c",
          "litify_pm__Phone_Other__c",
          "litify_pm__Phone_Work__c",
          "BillingStreet",
          "BillingCity",
          "BillingState",
          "Billing_house_number__c",
          "BillingPostalCode",
          "BillingCountry",
        ],
      }),
    });

    if (!response.ok) throw new Error();
    return await response.json();
  } catch {
    return null;
  }
};

export const fetchSalesforceFiles = async (relatedToId, idToken) => {
  const url = `${API_BASE_URL}/salesforce/files?relatedToId=${relatedToId}`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (response.status === 403) return { files: [] };
  if (response.status === 500) {
    const t = await response.text();
    window.location.href = `/error?message=${encodeURIComponent(t)}`;
    return null;
  }

  if (!response.ok) throw new Error();
  return await response.json();
};

export const fetchDownloadUrl = async (fileId, idToken) => {
  const response = await fetch(
    `${API_BASE_URL}/salesforce/files/${fileId}/download`,
    { headers: { Authorization: `Bearer ${idToken}` } },
  );

  if (!response.ok) throw new Error();

  const data = await response.json();
  return data.signedUrl;
};

export const fetchClientPartyDetails = async (token, partyId) => {
  const url = `${API_BASE_URL}/bhp-client-portal/clients/parties/${partyId}`;

  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (response.status === 403) return null;
  if (response.status === 500) {
    const t = await response.text();
    window.location.href = `/error?message=${encodeURIComponent(t)}`;
    return null;
  }

  if (!response.ok) throw new Error();
  return await response.json();
};

export const fetchClientMatterDetails = async (token, matterId) => {
  const url = `${API_BASE_URL}/bhp-client-portal/clients/matters/${matterId}`;

  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (response.status === 403) return null;
  if (response.status === 500) {
    const t = await response.text();
    window.location.href = `/error?message=${encodeURIComponent(t)}`;
    return null;
  }

  if (!response.ok) throw new Error();
  return await response.json();
};

export const updateCoCoProfile = async (
  clientId,
  currentEmail,
  newEmail,
  newPhone,
  token,
) => {
  const url = `${API_BASE_URL}/bhp-client-portal/profiles/${clientId}`;

  const changes = {};
  if (newEmail) changes.email = newEmail;
  if (newPhone) changes.phone = newPhone;

  const data = { email: currentEmail, changes };

  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const t = await response.text();
    throw new Error(`Error ${response.status}: ${t || "Unknown error"}`);
  }

  return { success: true, status: response.status };
};

export const storeRefreshToken = async (refreshToken) => {
  const url = `${API_BASE_URL}/bhp-client-portal/auth/store`;

  await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
};

export const refreshAccessTokenApi = async () => {
  const url = `${API_BASE_URL}/bhp-client-portal/auth/refresh`;

  try {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
    });

    if (res.status === 401) return null;
    if (!res.ok) throw new Error();

    return await res.json();
  } catch {
    return null;
  }
};

export const clearRefreshToken = async () => {
  const url = `${API_BASE_URL}/bhp-client-portal/auth/clear`;

  await fetch(url, {
    method: "POST",
    credentials: "include",
  });
};

export const sendPortalEvent = async (
  eventType,
  partyId = null,
  metadata = null,
  token = null,
) => {
  const url = `${API_BASE_URL}/bhp-client-portal/events`;

  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({
      eventType,
      ...(partyId ? { partyId } : {}),
      ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {}),
    }),
  });

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    throw new Error(`Error ${response.status}: ${t || "Unknown error"}`);
  }

  return true;
};

export const sendSmsOtp = async (email) => {
  await new Promise((r) => setTimeout(r, 600));
  return true;
};

export const verifySmsOtp = async (email, otp) => {
  await new Promise((r) => setTimeout(r, 600));
  return otp === "123456" ? { success: true } : { success: false };
};

export const patchBhpClientContact = async (
  token,
  partyId,
  { email, phone, pixKey, cpf },
) => {
  const url = `${API_BASE_URL}/bhp-client-portal/clients/${partyId}`;

  if (!cpf) {
    throw new Error("CPF is required");
  }

  const payload = { cpf };

  if (email) payload.email = email;
  if (phone) payload.phone = phone;
  if (pixKey) payload.pixKey = pixKey;

  if (!payload.email && !payload.phone && !payload.pixKey) {
    throw new Error("No fields to update");
  }

  const response = await fetchWithRetry(url, {
    method: "PATCH",
    // Needed so the backend can Set-Cookie a fresh refresh token when this call changes
    // email/phone (that invalidates the old one) - see AuthContext.applyRefreshedTokens.
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (response.status === 403) {
    handle403Error();
    return null;
  }

  if (response.status === 500) {
    const t = await response.text();
    window.location.href = `/error?message=${encodeURIComponent(t)}`;
    return null;
  }

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    throw new Error(`Error ${response.status}: ${t || "Unknown error"}`);
  }

  const text = await response.text();
  if (!text) return { success: true };

  try {
    return JSON.parse(text);
  } catch {
    return { success: true };
  }
};

export const generateBhpRetainer = async (token, partyId, matterId) => {
  const qs = new URLSearchParams({
    relatedToId: String(partyId || ""),
  });

  const url = `${API_BASE_URL}/bhp-client-portal/clients/${partyId}/matters/${matterId}/retainers?${qs.toString()}`;
  const genericMessage =
    "Não foi possível atender a solicitação, tente mais tarde";

  let response;
  try {
    response = await fetchWithRetry(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
  } catch (err) {
    console.error("generateBhpRetainer network error:", err);
    throw new Error(genericMessage);
  }

  if (response.status === 403) {
    handle403Error();
    return null;
  }

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    console.error("generateBhpRetainer error:", response.status, t);
    throw new Error(genericMessage);
  }

  const text = await response.text().catch(() => "");
  if (!text) return { success: true };

  try {
    return JSON.parse(text);
  } catch {
    return { success: true };
  }
};

export const postBhpClientLoginIssue = async (payload) => {
  const url = `${API_BASE_URL}/bhp-client-portal/login-issues`;

  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    const message =
      t || "Não foi possível atender a solicitação, tente mais tarde";
    throw new Error(`Error ${response.status}: ${message}`);
  }

  const text = await response.text().catch(() => "");
  if (!text) return { success: true };

  try {
    return JSON.parse(text);
  } catch {
    return { success: true };
  }
};

export const postClientContactUs = async (token, payload) => {
  const url = `${API_BASE_URL}/bhp-client-portal/contact-us`;

  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (response.status === 403) {
    handle403Error();
    throw new Error("Sua sessão expirou. Entre novamente para enviar a mensagem.");
  }

  if (response.status === 500) {
    const t = await response.text().catch(() => "");
    throw new Error(t || "Não foi possível processar sua mensagem.");
  }

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    throw new Error(t || "Não foi possível enviar sua mensagem. Tente novamente.");
  }

  const text = await response.text().catch(() => "");
  if (!text) return { success: true };

  try {
    return JSON.parse(text);
  } catch {
    return { success: true };
  }
};

export const fetchClientRetainers = async (
  token,
  partyId,
  matterId,
  caseTypeName,
) => {
  const qs = new URLSearchParams({
    caseTypeName: String(caseTypeName || ""),
  });

  const url = `${API_BASE_URL}/bhp-client-portal/clients/${partyId}/matters/${matterId}/retainers?${qs.toString()}`;

  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (response.status === 403) return null;

  if (response.status === 500) {
    const t = await response.text().catch(() => "");
    throw new Error(
      t || "Não foi possível atender a solicitação, tente mais tarde",
    );
  }

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    throw new Error(`Error ${response.status}: ${t || "Unknown error"}`);
  }

  const text = await response.text().catch(() => "");
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    throw new Error("Invalid response from server");
  }
};

export const downloadClientRetainer = async (
  token,
  partyId,
  matterId,
  fileId,
  fileName,
) => {
  const qs = new URLSearchParams();
  if (fileName) {
    qs.set("fileName", fileName);
  }
  const query = qs.toString();
  const url = `${API_BASE_URL}/bhp-client-portal/clients/${partyId}/matters/${matterId}/retainers/${fileId}/download${
    query ? `?${query}` : ""
  }`;

  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (response.status === 403) return null;

  if (response.status === 500) {
    const t = await response.text().catch(() => "");
    window.location.href = `/error?message=${encodeURIComponent(t)}`;
    return null;
  }

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    throw new Error(`Error ${response.status}: ${t || "Unknown error"}`);
  }

  const data = await response.json();

  if (data?.url) {
    window.open(data.url, "_blank");
  }

  return data;
};

export const startSurveySession = async (
  token,
  { partyId, matterId, actionName = "Disputes_Management_Create_Case" },
) => {
  const url = `${API_BASE_URL}/bhp-client-portal/surveys/session`;

  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      partyId,
      matterId,
      actionName,
    }),
  });

  if (response.status === 403) {
    handle403Error();
    return null;
  }

  if (response.status === 500) {
    const t = await response.text().catch(() => "");
    window.location.href = `/error?message=${encodeURIComponent(t)}`;
    return null;
  }

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    throw new Error(`Error ${response.status}: ${t || "Unknown error"}`);
  }

  const text = await response.text().catch(() => "");
  if (!text) {
    throw new Error("Empty response from survey session endpoint");
  }

  try {
    const data = JSON.parse(text);

    return {
      invitationId: data?.invitationId || "",
      invitationLink: data?.invitationLink || "",
      caseId: data?.caseId || "",
      questionnaireId: data?.questionnaireId || "",
    };
  } catch {
    throw new Error("Invalid response from survey session endpoint");
  }
};

export const fetchSurveyAccessToken = async (token, invitationId, matterId) => {
  const url = `${API_BASE_URL}/bhp-client-portal/surveys/invitations/${invitationId}/access-token`;

  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      matterId,
    }),
  });

  if (response.status === 403) {
    handle403Error();
    return null;
  }

  if (response.status === 500) {
    const t = await response.text().catch(() => "");
    window.location.href = `/error?message=${encodeURIComponent(t)}`;
    return null;
  }

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    throw new Error(`Error ${response.status}: ${t || "Unknown error"}`);
  }

  const text = await response.text().catch(() => "");
  if (!text) {
    throw new Error("Empty response from survey token endpoint");
  }

  try {
    const data = JSON.parse(text);

    return {
      invitationId: data?.invitationId || invitationId,
      accessToken: data?.accessToken || "",
      expiresAt: data?.expiresAt || null,
    };
  } catch {
    throw new Error("Invalid response from survey token endpoint");
  }
};

export const fetchModules = async (token) => {
  const url = `${API_BASE_URL}/bhp-client-portal/modules`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
  if (response.status === 404) {
    const legacyNotifications = await fetchSurveyNotifications(token);
    return normalizeModulesResponse(legacyNotifications);
  }
  if (response.status === 403) {
    handle403Error();
    return null;
  }
  if (response.status === 500) {
    const t = await response.text().catch(() => "");
    window.location.href = `/error?message=${encodeURIComponent(t)}`;
    return null;
  }
  if (!response.ok) {
    const t = await response.text().catch(() => "");
    throw new Error(`Error ${response.status}: ${t || "Unknown error"}`);
  }
  const text = await response.text().catch(() => "");
  if (!text) return [];
  try {
    const data = JSON.parse(text);
    return normalizeModulesResponse(data);
  } catch {
    throw new Error("Invalid response from modules endpoint");
  }
};

export const disablePortalModule = async (
  token,
  { moduleId = "", module = "", instanceKey = "" } = {},
) => {
  const url = `${API_BASE_URL}/bhp-client-portal/modules/manage`;
  const payload = {
    action: "disable",
    ...(moduleId ? { moduleId } : {}),
    ...(module && instanceKey ? { module, instanceKey } : {}),
  };

  if (!payload.moduleId && !(payload.module && payload.instanceKey)) {
    throw new Error("Module identifier is required");
  }

  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (response.status === 403) {
    handle403Error();
    return null;
  }

  if (response.status === 404) {
    return { status: "disabled", rowsAffected: 0 };
  }

  if (response.status === 500) {
    const t = await response.text().catch(() => "");
    window.location.href = `/error?message=${encodeURIComponent(t)}`;
    return null;
  }

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    throw new Error(`Error ${response.status}: ${t || "Unknown error"}`);
  }

  const text = await response.text().catch(() => "");
  if (!text) return { status: "disabled", rowsAffected: 1 };

  try {
    return JSON.parse(text);
  } catch {
    return { status: "disabled", rowsAffected: 1 };
  }
};

export const recordPortalModuleConfirmation = async (
  token,
  { module = "", instanceKey = "", userId = "", contextJson = null } = {},
) => {
  if (!module || !instanceKey || !userId) {
    throw new Error("Module, instanceKey and userId are required");
  }

  const url = `${API_BASE_URL}/bhp-client-portal/modules/manage`;
  // Deliberately "create", never "update": the row is written once and its
  // original confirmation timestamp can never be overwritten by a later click.
  const payload = {
    action: "create",
    module,
    instanceKey,
    userId,
    enabled: true,
    ...(contextJson ? { contextJson } : {}),
  };

  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (response.status === 403) {
    handle403Error();
    return null;
  }

  // Already recorded - the first confirmation stands.
  if (response.status === 409) {
    return { status: "confirmed", alreadyRecorded: true };
  }

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    throw new Error(`Error ${response.status}: ${t || "Unknown error"}`);
  }

  return { status: "confirmed", alreadyRecorded: false };
};

export const fetchSurveyNotifications = async (token) => {
  const url = `${API_BASE_URL}/bhp-client-portal/surveys/notifications`;

  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (response.status === 403) {
    handle403Error();
    return null;
  }

  if (response.status === 500) {
    const t = await response.text().catch(() => "");
    window.location.href = `/error?message=${encodeURIComponent(t)}`;
    return null;
  }

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    throw new Error(`Error ${response.status}: ${t || "Unknown error"}`);
  }

  const text = await response.text().catch(() => "");
  if (!text) return [];

  try {
    const data = JSON.parse(text);
    return Array.isArray(data) ? data : [];
  } catch {
    throw new Error("Invalid response from survey notifications endpoint");
  }
};

export const fetchSurveyStatus = async (token, invitationId) => {
  const url = `${API_BASE_URL}/bhp-client-portal/surveys/invitations/${invitationId}/status`;

  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (response.status === 403) {
    handle403Error();
    return null;
  }

  if (response.status === 500) {
    const t = await response.text().catch(() => "");
    window.location.href = `/error?message=${encodeURIComponent(t)}`;
    return null;
  }

  if (!response.ok) {
    const t = await response.text().catch(() => "");
    throw new Error(`Error ${response.status}: ${t || "Unknown error"}`);
  }

  const text = await response.text().catch(() => "");

  if (!text) {
    return {
      invitationId: null,
      questionnaireCompleted: false,
      hasQuestionnaire: false,
      invitationStatus: null,
    };
  }

  try {
    const data = JSON.parse(text);

    return {
      invitationId: data?.invitationId || null,
      questionnaireCompleted: !!data?.questionnaireCompleted,
      hasQuestionnaire: !!data?.hasQuestionnaire,
      invitationStatus: data?.invitationStatus || null,
    };
  } catch {
    throw new Error("Invalid response from survey status endpoint");
  }
};

const readJsonBody = async (response) => {
  const text = await response.text().catch(() => "");
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

// The eSign routes (TDDS-3450) live only on the Terraform-managed bhp-client-portal gateway.
const esignEnvelopeUrl = (envelopeId, suffix) =>
  `${process.env.REACT_APP_GATEWAY_API_BASE_URL}/esign/envelopes/${encodeURIComponent(envelopeId)}/${suffix}`;

/**
 * Asks the BHP backend for a single-use eSign grant. These routes require the Cognito ACCESS token
 * (not the ID token). Never throws and never redirects: the embedded signing page is waiting for a
 * reply and shows its own error screen.
 * Resolves to `{ ok: true, grant, expiresAt }` or `{ ok: false, code }`.
 */
// Proxy-level failures are translated into codes the eSign page already explains to the signer;
// eSign's own codes (e.g. PORTAL_IDENTITY_MISMATCH) are relayed unchanged.
const ESIGN_PROXY_ERROR_CODES = {
  UNAUTHORIZED: "PORTAL_REAUTH_REQUIRED",
  ACCESS_TOKEN_REQUIRED: "PORTAL_REAUTH_REQUIRED",
  VALIDATION: "PORTAL_NOT_ALLOWED",
  NOT_FOUND: "PORTAL_NOT_ALLOWED",
  ESIGN_UNAVAILABLE: "PORTAL_CHECK_UNAVAILABLE",
};

export const fetchEsignPortalGrant = async (accessToken, envelopeId, matterId) => {
  if (!accessToken) return { ok: false, code: "PORTAL_REAUTH_REQUIRED" };
  try {
    const response = await fetch(esignEnvelopeUrl(envelopeId, "portal-grant"), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ matterId }),
    });
    const body = await readJsonBody(response);
    if (response.ok && typeof body?.grant === "string" && body.grant) {
      return { ok: true, grant: body.grant, expiresAt: body.expiresAt || null };
    }
    const code = typeof body?.error === "string" && body.error ? body.error : null;
    if (code) {
      return { ok: false, code: ESIGN_PROXY_ERROR_CODES[code] || code };
    }
    if (response.status === 401 || response.status === 403) {
      return { ok: false, code: "PORTAL_REAUTH_REQUIRED" };
    }
    return { ok: false, code: "PORTAL_CHECK_UNAVAILABLE" };
  } catch {
    return { ok: false, code: "PORTAL_CHECK_UNAVAILABLE" };
  }
};

/**
 * Current eSign status of an envelope for the signed-in client, or null when it cannot be read right
 * now. An envelope the client cannot see (404) is reported as NOT_FOUND so the dashboard hides it.
 */
export const fetchEsignEnvelopeStatus = async (accessToken, envelopeId) => {
  if (!accessToken || !envelopeId) return null;
  try {
    const response = await fetchWithRetry(esignEnvelopeUrl(envelopeId, "status"), {
      method: "GET",
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (response.status === 404) {
      return { envelopeId, status: "NOT_FOUND" };
    }
    if (!response.ok) return null;
    const body = await readJsonBody(response);
    return body && typeof body.status === "string" ? body : null;
  } catch {
    return null;
  }
};